<template>
  <div class="date-picker__wrapper">
    <slot
      v-if="useCustomInput"
      name="datePickerInputContainer"
      :set-show-date-picker="value => showDatePicker = value"
      :reset-selected-date="() => resetSelectedDate()"
      :trigger-id="'datePickerInputId'"
      :ev-blur="e => {
        if (!isSameWrapperOnBlur(e)) {
          showDatePicker = false;
        }
      }"
    />
    <m-date-picker-input
      v-else
      ref="datePickerInput"
      :selected-date="selectedDate"
      :selected-month="selectedMonth"
      :selected-year="selectedYear"
      :language="language"
      :date-format="readonly ? dateFormat : 'YYYY-MM-DD'"
      :state="state"
      :error-text="errorText"
      :hint-text="hintText"
      :disabled="getDisabled"
      :label-text="labelText"
      :placeholder="placeholder"
      :clearable="clearable"
      :readonly="readonly"
      @show-date-picker="showDatePicker = true"
      @hide-date-picker="($event) => {
        showDatePicker = false;
        $emit('blur', $event);
      }"
      @reset-selected-date="resetSelectedDate"
      @input="$emit('input', $event)"
    >
      <template #datePickerInputSlot="params">
        <slot name="datePickerInputCustom" :params="params" />
      </template>
    </m-date-picker-input>
    <m-date-picker-container
      v-if="showDatePicker && !getDisabled"
      ref="datePickerContainer"
      :previous-date-list="previousDateList"
      :next-date-list="nextDateList"
      :current-date-list="currentDateList"
      :picker-date="pickerDate"
      :picker-month="pickerMonth"
      :picker-year="pickerYear"
      :selected-date="selectedDate"
      :selected-month="selectedMonth"
      :selected-year="selectedYear"
      :date-picker-view="datePickerView"
      :disabled-dates="disabledDates"
      :language="language"
      class="position-absolute datepicker--no-outline"
      tabindex="0"
      @update-selected-date="updateSelectedDate"
      @update-selected-month="updateSelectedDate"
      @update-selected-year="updateSelectedDate"
      @update-month="updateMonth"
      @update-view="updateView"
      @hide-date-picker="showDatePicker = false"
    />
  </div>
</template>

<script>
import MDatePickerInput from './components/DatePickerInput';
import MDatePickerContainer from './components/DatePickerContainer';
import MDropdownMixins from './mixins';
import { createPopper } from '@popperjs/core';

export default {
  name: 'MDatePicker',
  components: {
    MDatePickerInput,
    MDatePickerContainer,
  },
  mixins: [
    MDropdownMixins,
  ],
  props: {
    value: {
      type: [Date, String, Number],
      default: new Date(),
    },
    language: {
      type: String,
      default: 'en',
    },
    dateFormat: {
      type: String,
      default: 'DD MMM YYYY',
    },
    state: {
      type: Boolean,
      default: null,
    },
    errorText: {
      type: String,
      default: '',
    },
    hintText: {
      type: String,
      default: '',
    },
    clickableReadonly: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
      default: null,
    },
    labelText: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    placement: {
      type: String,
      default: 'right',
    },
    offsetY: {
      type: Number,
      default: 4,
    },
    offsetX: {
      type: Number,
      default: 0,
    },
    disablePastDates: {
      type: Boolean,
      default: true,
    },
    clearable: {
      type: Boolean,
      default: true,
    },
    useCustomInput: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: true,
    },
  },
  data: () => ({
    showDatePicker: false,
    popperInstance: null,
    previousDateList: [],
    nextDateList: [],
    currentDateList: [],
    pickerDate: null,
    pickerMonth: null,
    pickerYear: null,
    selectedDate: null,
    selectedMonth: null,
    selectedYear: null,
    datePickerView: 'day',
    disabledDates: null,
  }),
  watch: {
    disabled() {
      this.showDatePicker = false;
    },
    clickableReadonly() {
      this.showDatePicker = false;
    },
    showDatePicker(val) {
      this.$nextTick(() => {
        if (val) {
          if (!this.getDisabled) {
            this.resetPopperInstance();
          }
          this.initSelectedDate();
          this.initPickerDate();
          this.initDateList();
        }
      });
    },
    value() {
      this.initSelectedDate();
      this.initPickerDate();
      this.initDateList();
    },
  },
  computed: {
    getDisabled() {
      if (this.disabled !== null) {
        return this.disabled;
      }
      return !this.clickableReadonly;
    },
  },
  mounted() {
    this.initSelectedDate();
    this.initPickerDate();
    this.initDateList();
    this.initDisabledDates();
  },
  methods: {
    updateView(view) {
      this.datePickerView = view;
    },
    updateMonth(newOffset) {
      const date = new Date(this.pickerYear, this.pickerMonth + newOffset);
      this.pickerMonth = date.getMonth();
      this.pickerYear = date.getFullYear();
      this.initDateList();
    },
    updateSelectedDate(val) {
      const { date, month, year, from } = val;
      if (from === 'datePicker') {
        this.selectedDate = date;
        this.selectedMonth = month;
        this.selectedYear = year;
        this.pickerDate = date;
        this.pickerMonth = month;
        this.pickerYear = year;
        this.showDatePicker = false;
        this.$emit('input', new Date(year, month, date));
      } else if (from === 'monthPicker') {
        this.pickerMonth = month;
        this.pickerYear = year;
        this.updateView('day');
      } else {
        this.pickerYear = year;
        this.updateView('month');
      }
      this.initDateList();
    },
    resetSelectedDate() {
      this.selectedDate = null;
      this.selectedMonth = null;
      this.selectedYear = null;
      this.showDatePicker = false;
      this.$emit('input', null);
    },
    initSelectedDate() {
      if (this.value !== null) {
        const date = new Date(this.value);
        this.selectedDate = date.getDate();
        this.selectedMonth = date.getMonth();
        this.selectedYear = date.getFullYear();
      } else {
        this.selectedDate = null;
        this.selectedMonth = null;
        this.selectedYear = null;
      }
    },
    initPickerDate() {
      const today = new Date();
      this.pickerYear = this.selectedYear !== null ? this.selectedYear : today.getFullYear();
      this.pickerMonth = this.selectedMonth !== null ? this.selectedMonth : today.getMonth();
      this.pickerDate = this.selectedDate !== null ? this.selectedDate : today.getDate();
    },
    initDateList() {
      this.previousDateList = [];
      this.nextDateList = [];
      this.currentDateList = [];

      const firstDate = new Date(this.pickerYear, this.pickerMonth);
      const lastDate = new Date(this.pickerYear, this.pickerMonth + 1, 0);

      const firstDateDay = firstDate.getDay();
      const lastDateDay = lastDate.getDay();

      // Generate list of date from previous month
      const previousMonthDuration = new Date(this.pickerYear, this.pickerMonth, 0).getDate();
      for (let i = firstDateDay - 1; i >= 0; i -= 1) {
        this.previousDateList.push(previousMonthDuration - i);
      }

      // Generate list of date from next month
      const lastDayOnWeek = 6;
      for (let j = 1; j <= lastDayOnWeek - lastDateDay; j += 1) {
        this.nextDateList.push(j);
      }

      // Genrate list of date for current month
      this.currentDateList = Array.from({ length: lastDate.getDate() }, (v, k) => k + 1);
    },
    resetPopperInstance() {
      if (this.popperInstance) {
        this.popperInstance.destroy();
        this.popperInstance = null;
      }
      const popperPlacement = {
        left: 'bottom-start',
        right: 'bottom-end',
      };
      const datePickerDistance = this.offsetY;
      const datePickerSkid = this.offsetX;
      const triggerEl = this.$refs.datePickerInput ? this.$refs.datePickerInput.$el : this.$el.querySelector('#datePickerInputId');
      const datePickerContainerEl = this.$refs.datePickerContainer.$el;
      this.popperInstance = createPopper(triggerEl, datePickerContainerEl, {
        placement: popperPlacement[this.placement],
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [datePickerSkid, datePickerDistance],
            },
          },
          {
            name: 'flip',
          },
        ],
      });
    },
    initDisabledDates() {
      if (this.disablePastDates) {
        this.disabledDates = { to: new Date().setHours(0,0,0,0) };
      }
      else this.disabledDates = null;
    },
  },
};
</script>
